import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Finance Vault — money clarity with raw data kept private and vault-gated.
 *
 * All amounts are stored as INTEGER MINOR UNITS (e.g. cents) so arithmetic is
 * exact — never floating-point money. An Account holds an opening balance; a
 * Transaction is a signed-by-kind movement against one account. Balances and
 * period summaries are pure functions, so they are deterministic and testable.
 *
 * Privacy: finance records default to the `finance_vault` class. They are
 * local-first and intended to sit behind a step-up lock (see Security).
 */

export type AccountType = 'cash' | 'bank' | 'credit' | 'investment' | 'asset' | 'liability';
export const ACCOUNT_TYPES: readonly AccountType[] = [
  'cash',
  'bank',
  'credit',
  'investment',
  'asset',
  'liability',
] as const;

export interface Account extends RecordEnvelope {
  readonly kind: 'account';
  readonly name: string;
  readonly accountType: AccountType;
  /** ISO 4217-ish currency code; not validated, owner-defined. */
  readonly currency: string;
  /** Opening balance in minor units (can be negative for liabilities). */
  readonly openingBalanceMinor: number;
}

export interface AccountInput {
  readonly name: string;
  readonly accountType?: AccountType;
  readonly currency?: string;
  readonly openingBalanceMinor?: number;
  readonly privacyClass?: PrivacyClass;
}

export type TransactionKind = 'income' | 'expense';
export const TRANSACTION_KINDS: readonly TransactionKind[] = ['income', 'expense'] as const;

/** Payment rail a transaction moved over (from statement narration intelligence). */
export type TransactionRail = 'UPI' | 'NEFT' | 'IMPS' | 'RTGS' | 'ATM' | 'CARD' | 'CASH' | 'INTEREST' | 'CHARGE' | 'OTHER';
export const TRANSACTION_RAILS: readonly TransactionRail[] = [
  'UPI', 'NEFT', 'IMPS', 'RTGS', 'ATM', 'CARD', 'CASH', 'INTEREST', 'CHARGE', 'OTHER',
] as const;

export interface Transaction extends RecordEnvelope {
  readonly kind: 'transaction';
  readonly accountId: EntityId;
  readonly txnKind: TransactionKind;
  /** Always >= 0; direction is carried by txnKind. Minor units. */
  readonly amountMinor: number;
  readonly category: string;
  /** ISO date the transaction is counted under. */
  readonly occurredOn: string;
  readonly note: string;
  // ── Optional fields populated by statement imports (manual entries omit them) ──
  /** Precise time when known (e.g. from Google Pay); else null. */
  readonly occurredAt: string | null;
  /** Human-readable other party (payee/payer). */
  readonly counterparty: string | null;
  /** UPI RRN / bank reference number — the cross-source dedup key. */
  readonly externalReference: string | null;
  readonly rail: TransactionRail | null;
  /** Running balance after this row (statement reconciliation), minor units. */
  readonly balanceAfterMinor: number | null;
  /** Idempotency key; a re-import with the same fingerprint is a no-op. */
  readonly importFingerprint: string | null;
  /** Provenance: the import batch that created this row, if any. */
  readonly importBatchId: EntityId | null;
}

export interface TransactionInput {
  readonly accountId: EntityId;
  readonly txnKind: TransactionKind;
  readonly amountMinor: number;
  readonly category?: string;
  readonly occurredOn?: string;
  readonly note?: string;
  readonly occurredAt?: string | null;
  readonly counterparty?: string | null;
  readonly externalReference?: string | null;
  readonly rail?: TransactionRail | null;
  readonly balanceAfterMinor?: number | null;
  readonly importFingerprint?: string | null;
  readonly importBatchId?: EntityId | null;
  readonly privacyClass?: PrivacyClass;
}

/**
 * One imported statement run: which account + file, when, and how many rows it
 * created vs skipped (already present). Makes imports auditable and re-runnable.
 */
export interface ImportBatch extends RecordEnvelope {
  readonly kind: 'import_batch';
  readonly accountId: EntityId;
  /** "sbi-xlsx", "gpay-pdf", "canonical-ledger", etc. */
  readonly sourceLabel: string;
  /** Names of the source files this batch ingested. */
  readonly sourceFiles: readonly string[];
  readonly rowsSeen: number;
  readonly rowsCreated: number;
  readonly rowsSkipped: number;
  readonly importedOn: string;
  readonly note: string;
}

/** Signed contribution of a transaction to a balance (income +, expense -). */
export function signedAmountMinor(txn: Pick<Transaction, 'txnKind' | 'amountMinor'>): number {
  const magnitude = Math.max(0, Math.trunc(txn.amountMinor));
  return txn.txnKind === 'income' ? magnitude : -magnitude;
}

/** Current balance of an account = opening + sum of its transactions. Pure. */
export function accountBalanceMinor(
  account: Pick<Account, 'id' | 'openingBalanceMinor'>,
  transactions: readonly Pick<Transaction, 'accountId' | 'txnKind' | 'amountMinor'>[],
): number {
  let balance = account.openingBalanceMinor;
  for (const t of transactions) {
    if (String(t.accountId) === String(account.id)) balance += signedAmountMinor(t);
  }
  return balance;
}

export interface FinanceSummary {
  readonly incomeMinor: number;
  readonly expenseMinor: number;
  readonly netMinor: number;
  readonly byCategory: Record<string, number>;
}

/** Income/expense/net + per-category net over [fromIso, toIso] (inclusive dates). Pure. */
export function summarizeTransactions(
  transactions: readonly Pick<Transaction, 'txnKind' | 'amountMinor' | 'category' | 'occurredOn'>[],
  fromIso: string,
  toIso: string,
): FinanceSummary {
  const from = fromIso.slice(0, 10);
  const to = toIso.slice(0, 10);
  let incomeMinor = 0;
  let expenseMinor = 0;
  const byCategory: Record<string, number> = {};
  for (const t of transactions) {
    const day = t.occurredOn.slice(0, 10);
    if (day < from || day > to) continue;
    const signed = signedAmountMinor(t);
    if (t.txnKind === 'income') incomeMinor += Math.max(0, Math.trunc(t.amountMinor));
    else expenseMinor += Math.max(0, Math.trunc(t.amountMinor));
    const cat = t.category || 'uncategorized';
    byCategory[cat] = (byCategory[cat] ?? 0) + signed;
  }
  return { incomeMinor, expenseMinor, netMinor: incomeMinor - expenseMinor, byCategory };
}

/** Total net worth across accounts (opening + their transactions). Pure. */
export function netWorthMinor(
  accounts: readonly Pick<Account, 'id' | 'openingBalanceMinor'>[],
  transactions: readonly Pick<Transaction, 'accountId' | 'txnKind' | 'amountMinor'>[],
): number {
  return accounts.reduce((sum, a) => sum + accountBalanceMinor(a, transactions), 0);
}

/** Format minor units as a human string. Display-only; never used for math. */
export function formatMinor(amountMinor: number, currency = 'USD'): string {
  const sign = amountMinor < 0 ? '-' : '';
  const abs = Math.abs(amountMinor);
  const major = Math.floor(abs / 100);
  const minor = String(abs % 100).padStart(2, '0');
  return `${sign}${currency} ${major}.${minor}`;
}

// ─────────────────────────── Statement import ───────────────────────────

/** A normalized row offered to the import service (one bank/UPI transaction). */
export interface ImportRow {
  readonly occurredOn: string;
  readonly occurredAt?: string | null;
  readonly txnKind: TransactionKind;
  readonly amountMinor: number;
  readonly counterparty?: string | null;
  readonly externalReference?: string | null;
  readonly rail?: TransactionRail | null;
  readonly balanceAfterMinor?: number | null;
  readonly narration: string;
  readonly category?: string;
}

/**
 * Deterministic idempotency key for an imported row. Two rows with the same
 * fingerprint are the same transaction, so re-importing overlapping statements
 * is a no-op. Pure (no hashing needed — string equality is the contract).
 */
export function buildImportFingerprint(accountId: EntityId, row: ImportRow): string {
  return [
    String(accountId),
    row.occurredOn,
    row.txnKind,
    String(Math.max(0, Math.trunc(row.amountMinor))),
    row.externalReference ?? '',
    row.narration.replace(/\s+/g, ' ').trim(),
    row.balanceAfterMinor == null ? '' : String(row.balanceAfterMinor),
  ].join('|');
}

/** Bank account identity = stable hash-free key of account number + IFSC. */
export function bankIdentityKey(accountNumber: string | null, ifsc: string | null): string | null {
  const acc = (accountNumber ?? '').replace(/\s+/g, '').toUpperCase();
  const code = (ifsc ?? '').replace(/\s+/g, '').toUpperCase();
  if (!acc || !code || /[X*•]/.test(acc)) return null;
  return `${acc}|${code}`;
}

/** Lightweight category inference from a statement narration. Pure. */
export function inferTransactionCategory(narration: string, txnKind: TransactionKind): string {
  const n = narration.toLowerCase();
  if (/salary|stipend|refund|reversal|cashback/.test(n)) return txnKind === 'income' ? 'income' : 'refund';
  if (/rent|hostel|housing/.test(n)) return 'housing';
  if (/petrol|fuel|hp |iocl|bharat petroleum/.test(n)) return 'fuel';
  if (/kirana|store|mart|grocery|dairy|bakery|vegetable/.test(n)) return 'groceries';
  if (/swiggy|zomato|restaurant|hotel|cafe|food/.test(n)) return 'food';
  if (/airtel|jio|vodafone|recharge|mobile|broadband/.test(n)) return 'utilities';
  if (/movie|ticket|district|bookmy/.test(n)) return 'entertainment';
  if (/medical|hospital|pharmacy|chemist|clinic|ayurvedic/.test(n)) return 'health';
  if (/redbus|irctc|uber|ola|bus|train|travel|flight/.test(n)) return 'travel';
  if (/atm|cash wdl|withdrawal/.test(n)) return 'cash';
  if (/charge|fee|gst|sms|annual/.test(n)) return 'fees';
  if (/amazon|flipkart|myntra|ecom/.test(n)) return 'shopping';
  return txnKind === 'income' ? 'income' : 'other';
}
